import React from 'react';
import { Remove } from '@mui/icons-material';
import dayjs from 'dayjs';
import { type FormikErrors, useFormikContext } from 'formik';
import type { FundingPlanFormValue, FundingPlan } from './types';
import { PAGE_TEXT } from '../../lang';
import { DatePicker, IconButton, TextField } from '../../../../../components';

type AccountCodesProps = {
  index: number;
  isViewMode: boolean;
  handleDeleteFundingAccount: () => void;
};

const FUNDING_TEXT = PAGE_TEXT.FUNDING_SOURCE;
export const AccountCodes: React.FunctionComponent<AccountCodesProps> = (
  props: AccountCodesProps,
) => {
  const {
    index: fundingPlanIndex,
    isViewMode,
    handleDeleteFundingAccount,
  } = props;

  const { values, handleChange, handleBlur, errors, touched, setFieldValue } =
    useFormikContext<FundingPlanFormValue>();

  const fundingPlanErrors = errors.fundingPlan as
    | Array<FormikErrors<FundingPlan>>
    | undefined;

  const calculateMonthDifference = (
    fromDate: Date | '',
    toDate: Date | '',
  ): number => {
    const start = dayjs(fromDate).startOf('month');
    const end = dayjs(toDate).startOf('month');
    if (!start.isValid() || !end.isValid()) return 0;
    if (end.isBefore(start)) return 0;
    return (end.year() - start.year()) * 12 + (end.month() - start.month()) + 1;
  };

  // display the number of months at the end of row to facilitate GS checking
  const fromDate = values?.fundingPlan?.[fundingPlanIndex]?.period?.from;
  const toDate = values?.fundingPlan?.[fundingPlanIndex]?.period?.to;
  const monthsDiff = calculateMonthDifference(fromDate, toDate);

  return (
    <div className="bg-[#ECECEC] py-4 pr-2 min-w-[1000px]">
      <div
        className={`grid grid-cols-[8fr,7fr,7fr,3fr] gap-2 content-center pl-[24px]`}
      >
        <TextField
          name={`fundingPlan[${fundingPlanIndex}].accountCode`}
          label={FUNDING_TEXT.ACCOUNT.CODE}
          value={values?.fundingPlan?.[fundingPlanIndex]?.accountCode || ''}
          InputLabelProps={{ shrink: true }}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={isViewMode}
          error={
            touched?.fundingPlan?.[fundingPlanIndex]?.accountCode &&
            Boolean(fundingPlanErrors?.[fundingPlanIndex]?.accountCode)
          }
          helperText={
            touched?.fundingPlan?.[fundingPlanIndex]?.accountCode &&
            fundingPlanErrors?.[fundingPlanIndex]?.accountCode
          }
        />
        <DatePicker
          openTo="month"
          views={['year', 'month']}
          localTime={false}
          format="DD/MM/YYYY"
          disabled={isViewMode}
          inputDisabled={true}
          name={`fundingPlan[${fundingPlanIndex}].period.from`}
          label={FUNDING_TEXT.ACCOUNT.FROM}
          value={
            values?.fundingPlan?.[fundingPlanIndex]?.period?.from !== ''
              ? dayjs(
                  values?.fundingPlan?.[fundingPlanIndex]?.period?.from,
                ).startOf('month')
              : null
          }
          InputLabelProps={{ shrink: true }}
          inputProps={{
            max: values?.fundingPlan?.[fundingPlanIndex]?.period?.to,
          }}
          onChange={(date: any) => {
            void setFieldValue(
              `[fundingPlan.${fundingPlanIndex}].period.from`,
              date,
            );
          }}
          onBlur={handleBlur}
          error={
            touched?.fundingPlan?.[fundingPlanIndex]?.period?.from &&
            Boolean(fundingPlanErrors?.[fundingPlanIndex]?.period?.from)
          }
          helperText={
            touched?.fundingPlan?.[fundingPlanIndex]?.period?.from &&
            fundingPlanErrors?.[fundingPlanIndex]?.period?.from
          }
        />
        <DatePicker
          openTo="month"
          views={['year', 'month']}
          localTime={false}
          format="DD/MM/YYYY"
          inputDisabled={true}
          disabled={isViewMode}
          name={`fundingPlan[${fundingPlanIndex}].period.to`}
          label={FUNDING_TEXT.ACCOUNT.TO}
          value={
            values?.fundingPlan?.[fundingPlanIndex]?.period?.to !== ''
              ? dayjs(
                  values?.fundingPlan?.[fundingPlanIndex]?.period?.to,
                ).endOf('month')
              : null
          }
          InputLabelProps={{ shrink: true }}
          inputProps={{
            min: values?.fundingPlan?.[fundingPlanIndex]?.period?.from,
          }}
          onChange={(date: any) => {
            void setFieldValue(
              `fundingPlan.[${fundingPlanIndex}].period.to`,
              date,
            );
          }}
          onBlur={handleBlur}
          error={
            touched?.fundingPlan?.[fundingPlanIndex]?.period?.to &&
            Boolean(fundingPlanErrors?.[fundingPlanIndex]?.period?.to)
          }
          helperText={
            touched?.fundingPlan?.[fundingPlanIndex]?.period?.to &&
            fundingPlanErrors?.[fundingPlanIndex]?.period?.to
          }
        />
        <div className="px-2 py-4 flex flex-row items-start justify-between w-full gap-2">
          <div>
            {!isViewMode && (
              <IconButton
                onClick={handleDeleteFundingAccount}
                sx={{
                  width: 20,
                  height: 20,
                  backgroundColor: '#951D1D',
                  color: 'white',
                  ':hover': {
                    backgroundColor: '#951D1D',
                  },
                }}
                icon={<Remove />}
              />
            )}
          </div>
          <small className="text-[#951D1D]">{monthsDiff}&nbsp;month(s)</small>
        </div>
      </div>
    </div>
  );
};
